"use client";

import React from "react";
import { ArrowRight, Building2, CircleCheck, Lock, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputStyles = "flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
const textareaStyles = "flex min-h-[80px] w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
function EnterpriseSalesForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center animate-in fade-in zoom-in duration-500">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="h-6 w-6 text-primary" />
        </div>
        <h4 className="font-default text-lg font-semibold text-foreground mb-2">
          Request Received
        </h4>
        <p className="text-muted-foreground text-sm max-w-[250px]">
          {successMessage}
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Full Name</FormLabel>
            <FormControl>
              <input {...field} className={inputStyles} placeholder="Jane Doe" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Work email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Work Email</FormLabel>
            <FormControl>
              <input {...field} type="email" className={inputStyles} placeholder="jane@company.com" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Company name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Company</FormLabel>
            <FormControl>
              <input {...field} className={inputStyles} placeholder="Acme Corp" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="requirements" render={({
      field
    }) => <FormItem>
            <FormLabel>Specific Requirements (Optional)</FormLabel>
            <FormControl>
              <textarea {...field} className={textareaStyles} placeholder="Tell us about your infrastructure needs..." />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm text-center">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full mt-6 h-11 text-base font-medium">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Submitting...
          </> : "Request Enterprise Access"}
      </Button>
    </Form>;
}
export default function EnterpriseHorizon() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const features = [{
    icon: <Lock className="h-5 w-5 text-primary" />,
    title: "Private On-Premise",
    description: "Deploy OceanClaw entirely within your own secure infrastructure. Maintain absolute control over your data and AI interactions without external dependencies."
  }, {
    icon: <Building2 className="h-5 w-5 text-primary" />,
    title: "Tailored Workflows",
    description: "Custom configurations designed specifically for your organization's unique operational needs, security protocols, and specialized use cases."
  }];
  return <section id="enterprise-horizon" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-primary/5 blur-[120px] rounded-[100%]" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      </div>

      <div ref={ref} className={`relative z-10 container mx-auto px-4 md:px-6 max-w-5xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
            Enterprise Horizon
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Scale OceanClaw's next-generation AI capabilities across your
            organization. We support custom enterprise deployments designed for
            organizations that demand absolute control and security.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {features.map((feature, i) => <div key={i} data-index={i} className="group relative p-8 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm hover:bg-card/80 transition-colors duration-300">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-background border border-border/50 shadow-sm group-hover:scale-110 transition-transform duration-300">
                {feature.icon}
              </div>
              <h3 className="font-default text-xl font-semibold text-foreground mb-3">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </div>)}
        </div>

        <div className="flex justify-center">
          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="h-14 px-8 text-base font-semibold rounded-full shadow-[0_0_40px_-10px_rgba(var(--primary),0.5)] hover:shadow-[0_0_60px_-15px_rgba(var(--primary),0.7)] transition-all duration-300">
                Contact Sales
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="dark bg-background text-foreground sm:max-w-[425px] border-border/50 shadow-2xl">
              <DialogHeader>
                <DialogTitle className="font-default text-2xl">
                  Enterprise Inquiry
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Connect with our enterprise team to discuss custom deployments
                  and tailored on-premise solutions.
                </DialogDescription>
              </DialogHeader>

              <FormProvider formId="enterprise-sales-form" sectionName="Enterprise Horizon" defaultValues={{
              name: "",
              email: "",
              company: "",
              requirements: ""
            }} submitText="Submit Request" successMessage="Thank you. Our enterprise team will contact you shortly to discuss your requirements.">
                <EnterpriseSalesForm />
              </FormProvider>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>;
}